import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, withQuery, quantity, date, money, STATUS_LABEL, STATUS_CLASS } from '../api.js';
import { useAuth } from '../App.jsx';
import { SelectedDocumentLines } from './NewDocument.jsx';
import { Pager } from './Products.jsx';
import Suggestions, { useSuggestKeys } from '../components/Suggestions.jsx';

/**
 * Прехвърляне на стока между складовете.
 *
 * Лайненият склад е далеч от офиса и стоката се носи периодично. Затова
 * прехвърлянето е отделен екран, а не разновидност на доставката: тук
 * няма контрагент, няма цени и няма плащане — само какво излиза оттук и
 * влиза там.
 *
 * Наличността се показва в склада ИЗТОЧНИК и се проверява спрямо него:
 * най-честата грешка е да се прехвърли нещо, което го няма.
 *
 * Работи като продажбата и доставката (01.10.2026): редовете се трупат,
 * документът се записва веднъж накрая с „Запис“. Обикновено се носят
 * много стоки наведнъж, затова започнатото се пази в браузъра —
 * презареждане или излизане от екрана не губи редовете.
 */
const CHERNOVA = 'tefter.transferDraft';
function prochetiChernova() {
  try { return JSON.parse(localStorage.getItem(CHERNOVA) ?? 'null'); } catch { return null; }
}
/*
 * Корекцията (?edit=<id>) и новото прехвърляне са отделни екрани — при
 * преминаване от едното към другото компонентът се зарежда наново. Иначе
 * редовете на коригираното прехвърляне оставаха на екрана и се записваха
 * като чернова на новото (21.10.2026: редовете на № 0000000746 се появиха
 * в „Ново прехвърляне“).
 */
export default function Transfer() {
  const [searchParams] = useSearchParams();
  return <TransferEkran key={searchParams.get('edit') ?? 'novo'} />;
}

function TransferEkran() {
  const navigate = useNavigate();
  // В адреса (?view=old), не в локално състояние — иначе "Назад" след
  // отваряне на старо прехвърляне се връщаше на празната форма, не на
  // списъка, от който е тръгнал операторът.
  const [searchParams] = useSearchParams();
  // Корекция на записано прехвърляне (13.10.2026): ?edit=<id>. Тогава
  // браузърната чернова не се чете и не се пише, а складовете са заключени.
  const editId = Number(searchParams.get('edit')) || null;
  const view = !editId && searchParams.get('view') === 'old' ? 'old' : 'new';
  const [warehouses, setWarehouses] = useState([]);
  const [editDoc, setEditDoc] = useState(null);
  const nachalo = useRef(editId ? null : prochetiChernova());
  const [fromId, setFromId] = useState(nachalo.current?.fromId ?? '');
  const [toId, setToId] = useState(nachalo.current?.toId ?? '');
  const [note, setNote] = useState(nachalo.current?.note ?? '');
  const [lines, setLines] = useState(nachalo.current?.lines ?? []);
  const [samoNalichni, setSamoNalichni] = useState(true);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = useRef(null);
  const qtyRefs = useRef(new Map());
  const [fokus, setFokus] = useState(null);

  // Започнатото прехвърляне се пази, докато не се запише или изчисти.
  useEffect(() => {
    if (editId) return;
    try {
      if (lines.length === 0 && !note) localStorage.removeItem(CHERNOVA);
      else localStorage.setItem(CHERNOVA, JSON.stringify({ fromId, toId, note, lines }));
    } catch { /* без localStorage — просто не се пази */ }
  }, [fromId, toId, note, lines, editId]);

  // Редовете на прехвърлянето за корекция. „Налично" е наличното в
  // източника плюс вече прехвърленото по реда — то се връща при запис.
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get(`/api/documents/${editId}`);
        if (d.doc_type !== 'transfer' || d.status !== 'confirmed') {
          throw new Error('Поправя се само записано прехвърляне');
        }
        const redove = await Promise.all(d.items
          .filter((it) => Number(it.quantity) > 0)
          .map(async (it) => {
            const p = await api.get(`/api/products/${it.product_id}`).catch(() => null);
            const vSklada = Number((p?.stock ?? [])
              .find((s) => Number(s.warehouse_id) === Number(d.warehouse_id))?.quantity ?? 0);
            return {
              key: `e${it.id}`,
              dbId: it.id,
              productId: it.product_id,
              description: it.description,
              group: null,
              unit: it.unit,
              available: vSklada + Number(it.quantity),
              quantity: String(Number(it.quantity)),
            };
          }));
        if (cancelled) return;
        setEditDoc(d);
        setFromId(String(d.warehouse_id));
        setToId(String(d.warehouse_to_id));
        setNote(d.note ?? '');
        setLines(redove);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [editId]);

  // След добавяне курсорът отива в количеството на реда — както в продажбата.
  useEffect(() => {
    if (!fokus) return;
    const el = qtyRefs.current.get(fokus);
    if (el) { el.focus(); el.select(); }
    setFokus(null);
  }, [fokus, lines]);

  useEffect(() => {
    api.get('/api/warehouses')
      .then((w) => {
        setWarehouses(w);
        // Ходи се до Лайнян склад и се носи стока във Варна — това е
        // обичайната посока, затова тя е и подредбата по подразбиране.
        const away = w.find((x) => /лайнян/i.test(x.name));
        const home = w.find((x) => /варна/i.test(x.name));
        if (editId) return;
        if (!nachalo.current?.fromId) setFromId(String((away ?? w[0])?.id ?? ''));
        if (!nachalo.current?.toId) setToId(String((home ?? w[1] ?? w[0])?.id ?? ''));
      })
      .catch((e) => setError(e.message));
  }, []);

  // Наличността се тегли за склада източник — тя решава какво може да
  // тръгне. Смени ли се складът, редовете си остават, но числото до тях
  // вече е за новия и това веднага си личи.
  useEffect(() => {
    if (query.trim().length < 2 || !fromId) { setHits([]); return undefined; }
    const t = setTimeout(() => {
      api.get(withQuery('/api/products', { q: query, warehouseId: Number(fromId), limit: 30 }))
        .then((r) => setHits(samoNalichni
          ? r.items.filter((p) => Number(p.quantity ?? 0) > 0) : r.items))
        .catch(() => setHits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [query, fromId, samoNalichni]);

  // Стрелки и Enter в падащия списък (09.10.2026).
  const klaviatura = useSuggestKeys(hits, add, (p) => p.group_name);

  function add(p) {
    // Същата стока втори път не прави нов ред — отива на съществуващия.
    const ima = lines.find((l) => l.productId === p.id);
    if (ima) {
      setFokus(ima.key);
    } else {
      const key = `${p.id}-${Date.now()}`;
      setLines((ls) => [...ls, {
        key,
        productId: p.id,
        description: p.name,
        group: p.group_name ?? null,
        unit: p.unit,
        available: Number(p.quantity ?? 0),
        quantity: '1',
      }]);
      setFokus(key);
    }
    setQuery('');
    setHits([]);
  }

  function izchisti() {
    if (lines.length && !window.confirm('Да се изчистят всички редове на започнатото прехвърляне?')) return;
    setLines([]);
    setNote('');
  }

  const patch = (key, v) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...v } : l)));

  const sameWarehouse = fromId && toId && fromId === toId;
  /*
   * Не се прехвърля повече, отколкото има в склада източник.
   *
   * Досега само жълт бадж "повече от наличното" предупреждаваше на реда,
   * а копчето "Прехвърли" оставаше активно — грешката идваше едва от
   * сървъра. Тук се проверява преди да се стигне дотам.
   */
  const nadNalichnost = lines.some((l) => Number(l.quantity) > Number(l.available));
  const ready = lines.length > 0 && !sameWarehouse && !nadNalichnost
    && lines.every((l) => Number(l.quantity) > 0);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (editId) {
        await api.patch(`/api/documents/${editId}/transfer-items`, {
          note: note || null,
          items: lines.map((l) => (l.dbId
            ? { id: l.dbId, quantity: Number(l.quantity || 0) }
            : {
              productId: l.productId, description: l.description, unit: l.unit,
              quantity: Number(l.quantity || 0),
            })),
        });
        navigate(`/documents/${editId}`);
        return;
      }
      const res = await api.post('/api/documents', {
        docType: 'transfer',
        status: 'confirmed',
        warehouseId: Number(fromId),
        warehouseToId: Number(toId),
        note: note || null,
        items: lines.map((l) => ({
          productId: l.productId,
          description: l.description,
          unit: l.unit,
          quantity: Number(l.quantity || 0),
          price: 0,
          vatRate: 0,
        })),
      });
      try { localStorage.removeItem(CHERNOVA); } catch { /* */ }
      navigate(`/documents/${res.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>{editId
          ? `Корекция на прехвърляне № ${editDoc?.doc_number ?? ''}`
          : 'Прехвърляне между складове'}</h1>
      </div>

      {!editId && <div className="tabs">
        <button className={`tab ${view === 'new' ? 'active' : ''}`}
          onClick={() => { if (view === 'old') navigate('/transfer'); }}>
          Ново прехвърляне
        </button>
        <button className={`tab ${view === 'old' ? 'active' : ''}`}
          onClick={() => navigate('/transfer?view=old')}>
          Стари прехвърляния
        </button>
      </div>}

      {view === 'old' ? <OldTransfers /> : (
      <>

      {error && <div className="error">{error}</div>}

      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label className="field">
              От склад
              <select value={fromId} disabled={!!editId} onChange={(e) => setFromId(e.target.value)}>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </label>
            <span style={{ paddingBottom: 10, fontSize: 20 }}>→</span>
            <label className="field">
              В склад
              <select value={toId} disabled={!!editId} onChange={(e) => setToId(e.target.value)}>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </label>
            <label className="field" style={{ flex: 1, minWidth: 220 }}>
              Забележка
              <input value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="например: занесено на 18.08" />
            </label>
          </div>
          {sameWarehouse && (
            <div className="error" style={{ marginTop: 12 }}>
              Двата склада са един и същ — няма какво да се прехвърли.
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">Редове</div>
        <div className="card-body" style={{ paddingBottom: 8 }}>
          <label className="field" style={{ position: 'relative', maxWidth: 460 }}>
            Добавяне на стока
            <span className="hint">наличността е за склада, от който тръгва</span>
            <input ref={search} type="search" autoComplete="off"
              value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="търсене по име, код или бърз код"
              onKeyDown={klaviatura.onKeyDown} />
            {hits.length > 0 && (
              <Suggestions
                activeId={klaviatura.activeId}
                items={hits}
                groupBy={(p) => p.group_name}
                onPick={add}
                render={(p) => (
                  <>
                    <strong>{p.name}</strong>
                    <span className={Number(p.quantity) > 0 ? 'badge' : 'badge muted'}
                      style={{ marginLeft: 8 }}>
                      {Number(p.quantity) > 0 ? `налични ${quantity(p.quantity)}` : 'няма'}
                    </span>
                    {lines.some((l) => l.productId === p.id) && (
                      <span className="badge" style={{ marginLeft: 6 }}>вече е добавена</span>
                    )}
                    {p.properties_text && (
                      <div className="muted" style={{ fontSize: 11 }}>{p.properties_text}</div>
                    )}
                  </>
                )}
              />
            )}
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, fontSize: 13 }}>
            <input type="checkbox" checked={samoNalichni} onChange={(e) => setSamoNalichni(e.target.checked)} />
            само с наличност
          </label>
        </div>

        <div className="table-wrap">
          <table className="line-editor">
            <thead>
              <tr>
                <th className="num" style={{ width: 34 }}>№</th>
                <th>Наименование</th>
                <th className="num" style={{ width: 130 }}>Налично</th>
                <th className="num" style={{ width: 120 }}>Прехвърля се</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, index) => {
                const short = Number(l.quantity) > l.available;
                return (
                  <tr key={l.key}>
                    <td className="num muted">{index + 1}</td>
                    <td>
                      {l.description}
                      {l.group && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>{l.group}</span>}
                    </td>
                    <td className="num muted">{quantity(l.available)} {l.unit}</td>
                    <td className="num">
                      <input className="qty num" type="number" step="0.001" min="0"
                        ref={(el) => { if (el) qtyRefs.current.set(l.key, el); else qtyRefs.current.delete(l.key); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search.current?.focus(); } }}
                        value={l.quantity}
                        onChange={(e) => patch(l.key, { quantity: e.target.value })} />
                      {short && (
                        <div className="badge warn" style={{ marginTop: 4 }}>
                          повече от наличното
                        </div>
                      )}
                    </td>
                    <td>
                      <button className="link" title="махни реда"
                        onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
              {lines.length === 0 && (
                <tr><td colSpan={5} className="empty">Добавете стока, за да започнете</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="card-body">
          {/* Количествата се пишат вдясно в таблицата; копчето стои
              под тях, а не в другия край на екрана. */}
          <div style={{
            display: 'flex', gap: 12, justifyContent: 'flex-end',
            alignItems: 'center', flexWrap: 'wrap',
          }}>
            <span className="hint" style={{ textAlign: 'right' }}>
              {nadNalichnost
                ? 'Има ред с количество над наличното в склада източник.'
                : editId
                  ? 'Складовете и датата остават; поправят се редовете и бележката.'
                : lines.length > 0
                  ? `${lines.length} ${lines.length === 1 ? 'стока' : 'стоки'} · започнатото се пази до „Запис“`
                  : 'Добавете всички стоки и накрая „Запис“.'}
            </span>
            {editId ? (
              <button onClick={() => navigate(`/documents/${editId}`)} disabled={busy}>Откажи</button>
            ) : lines.length > 0 && (
              <button onClick={izchisti} disabled={busy}>Изчисти</button>
            )}
            <button className="primary" onClick={save} disabled={!ready || busy}>
              {busy ? 'Запис…' : 'Запис'}
            </button>
          </div>
        </div>
      </div>
      </>
      )}
    </>
  );
}

/**
 * Последните прехвърляния — без филтри, само последните и „по-назад".
 *
 * Прехвърлянето няма контрагент, цена или плащане, затова обичайните
 * филтри на „Документи" (състояние, неплатени) не значат нищо тук.
 * Списъкът е само за да се намери и отвори конкретно прехвърляне.
 */
function OldTransfers() {
  const PAGE = 20;
  const [offset, setOffset] = useState(0);
  // Маркираният ред — отдолу се показва какво е прехвърлено (19.10.2026),
  // както в „Стари продажби“.
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selLoading, setSelLoading] = useState(false);
  const [selError, setSelError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const mojeKorekcia = ['admin', 'manager', 'operator'].includes(user?.role);

  useEffect(() => {
    if (!selectedId) { setSelectedDoc(null); return undefined; }
    let cancelled = false;
    setSelLoading(true);
    setSelError(null);
    api.get(`/api/documents/${selectedId}`)
      .then((d) => { if (!cancelled) setSelectedDoc(d); })
      .catch((e) => { if (!cancelled) setSelError(e.message); })
      .finally(() => { if (!cancelled) setSelLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(withQuery('/api/documents', { type: 'transfer', limit: PAGE, offset }))
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [offset]);

  return (
    <>
      {error && <div className="error">{error}</div>}
      <div className="card">
        <div className="table-wrap" style={{ maxHeight: 300 }}>
          <table className="compact">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Документ</th>
                <th>От склад</th>
                <th>В склад</th>
                <th>Състояние</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((d) => (
                <tr key={d.id} className={`clickable ${d.id === selectedId ? 'open' : ''}`}
                  onClick={() => setSelectedId(d.id)}>
                  <td className="muted">{date(d.doc_date)}</td>
                  <td><Link to={`/documents/${d.id}`} onClick={(e) => e.stopPropagation()}>{d.doc_number}</Link></td>
                  <td>{d.warehouse_name ?? '—'}</td>
                  <td>{d.warehouse_to_name ?? '—'}</td>
                  <td><span className={`badge ${STATUS_CLASS[d.status]}`}>{STATUS_LABEL[d.status]}</span></td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={5} className="empty">Няма прехвърляния</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pager total={data.total} offset={offset} setOffset={setOffset} loading={loading} page={PAGE} />
      </div>

      {selectedDoc?.status === 'confirmed' && mojeKorekcia && !selLoading && (
        <div className="toolbar" style={{ marginTop: 12 }}>
          <span className="muted">
            {selectedDoc.warehouse_name} → {selectedDoc.warehouse_to_name}
            {selectedDoc.note ? ` · ${selectedDoc.note}` : ''}
          </span>
          <div className="spacer" />
          <button onClick={() => navigate(`/transfer?edit=${selectedDoc.id}`)}>Корекция</button>
        </div>
      )}
      <SelectedDocumentLines doc={selectedDoc} loading={selLoading} error={selError} />
    </>
  );
}
